import { LayoutElement, ElementType, FloorPlanBounds } from '../types';
import { LAYOUT_3D_JSON_VERSION, LAYOUT_3D_OBJECT_COUNT } from './layout3dConstants';
import { FLOOR_3D_TYPES } from './layout3dFloor';

const LANE_EVERY = 4;

export type Layout3DProject = {
  version: string;
  generatedAt: string;
  boundsKey: string;
  objectCount: number;
  scaleFactor: number;
  bounds: FloorPlanBounds;
  elements: Record<string, LayoutElement>;
  elementOrder: string[];
};

type ZoneKind = 'storage' | 'lane-h' | 'lane-v' | 'intersection';

function classifyZone(col: number, row: number): ZoneKind {
  const isLaneCol = col % LANE_EVERY === 0;
  const isLaneRow = row % LANE_EVERY === 0;
  if (isLaneCol && isLaneRow) return 'intersection';
  if (isLaneCol) return 'lane-v';
  if (isLaneRow) return 'lane-h';
  return 'storage';
}

function typeForZone(zone: ZoneKind, index: number): ElementType {
  switch (zone) {
    case 'storage':
      return index % 5 === 0 ? 'circle' : 'stocker';
    case 'lane-h':
    case 'lane-v':
      return index % 4 === 0 ? 'rectangle' : 'oht-vehicle';
    case 'intersection': {
      const kinds: ElementType[] = ['stb', 'ohb', 'lifter'];
      return kinds[index % kinds.length]!;
    }
    default:
      return FLOOR_3D_TYPES[index % FLOOR_3D_TYPES.length]!;
  }
}

/** Distinct HSL color per object index and type family. */
export function colorForObjectIndex(index: number, type: ElementType): string {
  const familyHue: Record<ElementType, number> = {
    stocker: 168,
    'oht-vehicle': 28,
    'overhead-rail': 218,
    'overhead-curve': 218,
    'cross-rail': 218,
    stb: 272,
    ohb: 318,
    lifter: 4,
    'support-pillar': 45,
    'horizontal-beam': 45,
    'suspension-rod': 45,
    rectangle: 45,
    circle: 200,
    text: 220,
  };
  const base = familyHue[type] ?? 200;
  const hue = (base + ((index * 37) % 48)) % 360;
  const sat = 52 + (index % 28);
  const light = 38 + (index % 22);
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

export function computeBoundsKey(bounds: FloorPlanBounds, scaleFactor: number): string {
  return [
    Math.round(bounds.minX),
    Math.round(bounds.minY),
    Math.round(bounds.maxX),
    Math.round(bounds.maxY),
    Math.round(scaleFactor),
  ].join('_');
}

function createFloorElement(
  index: number,
  type: ElementType,
  cx: number,
  cy: number,
  cellSize: number,
  col: number,
  row: number,
  zone: ZoneKind
): LayoutElement {
  const id = `3d-${index}`;
  const color = colorForObjectIndex(index, type);
  const variant = index % 5;
  const isHorizontalLane = zone === 'lane-h';

  switch (type) {
    case 'stocker': {
      const size = Math.min(cellSize * (0.55 + variant * 0.04), 100 + variant * 8);
      return {
        id,
        layerId: '3d-generated',
        type,
        position: { x: cx - size / 2, y: cy - size / 2, z: 0 },
        rotation: (col + row) % 2 === 0 ? 0 : 90,
        dimensions: { width: size, height: size, depth: size * (1.1 + variant * 0.05) },
        metadata: {
          name: `Storage ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 0.94,
        },
      };
    }
    case 'oht-vehicle': {
      const ohtSize = Math.min(cellSize * (0.22 + variant * 0.02), 36 + variant * 2);
      return {
        id,
        layerId: '3d-generated',
        type,
        position: { x: cx - ohtSize / 2, y: cy - ohtSize / 2, z: 0 },
        rotation: isHorizontalLane ? variant * 5 : 90 + variant * 5,
        dimensions: { width: ohtSize, height: ohtSize, depth: 8 },
        metadata: {
          name: `OHT ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 1,
        },
      };
    }
    case 'rectangle': {
      const laneW = isHorizontalLane ? cellSize * 0.9 : Math.max(12, cellSize * 0.12);
      const laneH = isHorizontalLane ? Math.max(12, cellSize * 0.12) : cellSize * 0.9;
      return {
        id,
        layerId: '3d-generated',
        type,
        position: { x: cx - laneW / 2, y: cy - laneH / 2, z: 0 },
        rotation: isHorizontalLane ? 0 : 90,
        dimensions: { width: laneW, height: laneH, depth: 6 + variant },
        metadata: {
          name: `Lane ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 0.35,
        },
      };
    }
    case 'circle': {
      const diameter = Math.min(cellSize * 0.35, 48 + variant * 4);
      return {
        id,
        layerId: '3d-generated',
        type,
        position: { x: cx - diameter / 2, y: cy - diameter / 2, z: 0 },
        rotation: 0,
        dimensions: { width: diameter, height: diameter, depth: 8 + variant },
        metadata: {
          name: `Pad ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 0.5,
        },
      };
    }
    case 'lifter': {
      const sz = Math.min(cellSize * 0.38, 55 + variant * 6);
      return {
        id,
        layerId: '3d-generated',
        type,
        position: { x: cx - sz / 2, y: cy - sz / 2, z: 0 },
        rotation: variant * 15,
        dimensions: { width: sz, height: sz, depth: sz * 1.1 },
        metadata: {
          name: `Lifter ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 0.95,
        },
      };
    }
    case 'ohb':
    case 'stb':
    default: {
      const stbW = Math.min(cellSize * 0.32, 40 + variant * 3);
      const stbH = Math.min(cellSize * 0.2, 26 + variant * 2);
      return {
        id,
        layerId: '3d-generated',
        type: type === 'ohb' ? 'ohb' : 'stb',
        position: { x: cx - stbW / 2, y: cy - stbH / 2, z: 0 },
        rotation: 0,
        dimensions: { width: stbW, height: stbH, depth: 28 + variant * 2 },
        metadata: {
          name: `Buffer ${index + 1}`,
          locked: true,
          visible: true,
          color,
          opacity: 0.95,
        },
      };
    }
  }
}

/**
 * Generate a layout JSON with floor-only objects (no overhead rails) fitted to bounds.
 */
export function generateLayout3DProject(
  bounds: FloorPlanBounds,
  scaleFactor: number,
  objectCount = LAYOUT_3D_OBJECT_COUNT,
  boundsKey?: string
): Layout3DProject {
  const key = boundsKey ?? computeBoundsKey(bounds, scaleFactor);
  const floorW = bounds.maxX - bounds.minX;
  const floorH = bounds.maxY - bounds.minY;
  const margin = Math.max(60, Math.min(floorW, floorH) * 0.04);
  const originX = bounds.minX + margin;
  const originY = bounds.minY + margin;
  const gridW = floorW - margin * 2;
  const gridH = floorH - margin * 2;

  const aspect = gridW / Math.max(gridH, 1);
  const cols = Math.max(10, Math.ceil(Math.sqrt(objectCount * aspect)));
  const rows = Math.max(10, Math.ceil(objectCount / cols));
  const cellSize = Math.min(gridW / cols, gridH / rows);

  const elements: Record<string, LayoutElement> = {};
  const elementOrder: string[] = [];

  for (let i = 0; i < objectCount; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = originX + col * cellSize + cellSize / 2;
    const cy = originY + row * cellSize + cellSize / 2;
    const zone = classifyZone(col, row);
    const type = typeForZone(zone, i);
    const el = createFloorElement(i, type, cx, cy, cellSize, col, row, zone);
    elements[el.id] = el;
    elementOrder.push(el.id);
  }

  return {
    version: LAYOUT_3D_JSON_VERSION,
    generatedAt: new Date().toISOString(),
    boundsKey: key,
    objectCount: elementOrder.length,
    scaleFactor,
    bounds,
    elements,
    elementOrder,
  };
}
